import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../ui/table';
import {
  Shield, ShieldCheck, ShieldAlert, AlertTriangle, ExternalLink, RefreshCw,
  Loader2, Search, FileText, FileMinus2, CheckCircle2, ChevronLeft, ChevronRight,
  ArrowRight, CalendarRange,
} from 'lucide-react';
import FneCertifyModal from '../../FneCertifyModal';
import FneRefundModal from '../../FneRefundModal';
import FneStickerWidget from '../FneStickerWidget';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 10;

const KpiCard = ({ label, value, sublabel, tone = 'emerald', onClick, testId }) => {
  const palette = {
    emerald: { num: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100', icon: 'text-emerald-600' },
    amber: { num: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100', icon: 'text-amber-600' },
    rose: { num: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-100', icon: 'text-rose-600' },
    slate: { num: 'text-slate-700', bg: 'bg-slate-50', border: 'border-slate-100', icon: 'text-slate-600' },
  }[tone];
  return (
    <Card className={`${palette.border} hover:shadow-sm transition-shadow`} data-testid={testId}>
      <CardContent className="p-4 flex flex-col gap-2">
        <div className="flex items-start justify-between">
          <div className={`w-8 h-8 rounded-lg ${palette.bg} ${palette.icon} flex items-center justify-center`}>
            <Shield className="w-4 h-4" />
          </div>
        </div>
        <div>
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
          <div className={`text-3xl font-bold ${palette.num} leading-tight`}>{value}</div>
        </div>
        <div className="text-xs text-slate-500 leading-tight">{sublabel}</div>
        {onClick && (
          <button onClick={onClick} className={`mt-1 self-start inline-flex items-center gap-1 text-xs font-medium ${palette.num} hover:underline`} data-testid={`${testId}-link`}>
            Voir les lignes <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </CardContent>
    </Card>
  );
};

/** Toggle Spot ↔ Factures contrat — partagé entre les deux vues. */
export function FneModeSwitch({ mode, setMode }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="fne-mode-switch">
      <button
        onClick={() => setMode('spot')}
        data-testid="fne-mode-spot-btn"
        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
          mode === 'spot' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        Opérations spot
      </button>
      <button
        onClick={() => setMode('contract')}
        data-testid="fne-mode-contract-btn"
        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
          mode === 'contract' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        Factures contrat
      </button>
    </div>
  );
}

const TabPill = ({ active, label, onClick, count, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
      active ? 'text-emerald-700 border-emerald-600' : 'text-slate-500 border-transparent hover:text-slate-700'
    }`}
  >
    {label}
    {count !== undefined && <span className="ml-1.5 text-xs opacity-70">({count})</span>}
  </button>
);

const fmtMoney = (n) => (n ? `${Number(n).toLocaleString('fr-FR')} XOF` : '—');

const fmtShortDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
};

const fmtPeriod = (bp) => {
  if (!bp || (!bp.start && !bp.end)) return null;
  return `${fmtShortDate(bp.start)} → ${fmtShortDate(bp.end)}`;
};

const FneBadge = ({ status }) => {
  if (status === 'certified') {
    return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300"><ShieldCheck className="h-3 w-3 mr-1" />Certifiée</Badge>;
  }
  if (status === 'pending_fne') {
    return <Badge variant="outline" className="border-blue-400 text-blue-700 bg-blue-50"><Shield className="h-3 w-3 mr-1 animate-pulse" />En cours…</Badge>;
  }
  if (status === 'refunded_total' || status === 'refunded') {
    return <Badge variant="outline" className="border-slate-400 text-slate-600"><ShieldAlert className="h-3 w-3 mr-1" />Avoir total</Badge>;
  }
  if (status === 'refunded_partial') {
    return <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50"><ShieldAlert className="h-3 w-3 mr-1" />Avoir partiel</Badge>;
  }
  if (status === 'failed' || status === 'fne_error') {
    return <Badge variant="destructive"><ShieldAlert className="h-3 w-3 mr-1" />Échec</Badge>;
  }
  return <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50"><Shield className="h-3 w-3 mr-1" />Non certifiée</Badge>;
};

const INVOICE_STATUS_LABEL = {
  draft: 'Brouillon', validated: 'Validée', sent: 'Envoyée',
  paid: 'Payée', partially_paid: 'Partiel', overdue: 'En retard',
};

export default function FneContractInvoicesTab({ mode, setMode, env = 'dev', switchControl }) {
  const [filter, setFilter] = useState('not_certified');
  const [data, setData] = useState({ items: [], counts: {}, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [certifyTarget, setCertifyTarget] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = filter === 'all' ? '' : `&status=${filter}`;
      const { data: resp } = await axios.get(
        `${API}/fne/contract-invoices?page=${page}&pageSize=${PAGE_SIZE}${statusParam}`
      );
      setData(resp);
    } catch (err) {
      toast.error('Erreur chargement', { description: err?.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => { load(); }, [load]);

  const c = data.counts || {};
  const updateFilter = (next) => { setFilter(next); setPage(1); };

  const items = useMemo(() => {
    if (!search) return data.items || [];
    const q = search.toLowerCase();
    return (data.items || []).filter((it) =>
      (it.invoiceNumber || '').toLowerCase().includes(q)
      || (it.contractNumber || '').toLowerCase().includes(q)
      || (it.client?.companyName || '').toLowerCase().includes(q)
    );
  }, [data.items, search]);

  return (
    <div className="space-y-5 p-4 md:p-6" data-testid="fne-contract-invoices-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, ui-sans-serif, system-ui' }}>
            <Shield className="w-6 h-6 inline-block mr-1.5 -mt-1 text-emerald-600" />
            Certifications FNE — Factures contrat
            <Badge variant="outline" className="ml-2 text-[10px] align-middle">{env.toUpperCase()}</Badge>
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Certifiez les factures de contrat (récurrentes) auprès de la DGI, comme les opérations spot.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {switchControl || <FneModeSwitch mode={mode} setMode={setMode} />}
          <Button variant="outline" size="sm" onClick={load} className="h-9" data-testid="fne-contract-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Rafraîchir
          </Button>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50/60 border border-emerald-200 text-xs" data-testid="fne-contract-banner">
        <Shield className="w-3.5 h-3.5 text-emerald-700" />
        <span className="text-slate-700">
          Seules les factures <strong>validées</strong> ou <strong>envoyées</strong> (et payées) peuvent être certifiées.
          Une facture certifiée est verrouillée — seul un avoir FNE peut l&apos;annuler.
        </span>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard label="Prêtes à certifier" value={c.ready ?? 0} sublabel="Factures contrat éligibles maintenant" tone="emerald" onClick={() => updateFilter('ready')} testId="fne-contract-kpi-ready" />
        <KpiCard label="Bloquées" value={c.blocked ?? 0} sublabel="Brouillon, NCC manquant ou hors périmètre" tone="amber" onClick={() => updateFilter('not_certified')} testId="fne-contract-kpi-blocked" />
        <KpiCard label="Certifiées" value={c.certified ?? 0} sublabel="Factures certifiées auprès de la DGI" tone="slate" onClick={() => updateFilter('certified')} testId="fne-contract-kpi-certified" />
        <KpiCard label="Échecs" value={c.failed ?? 0} sublabel="Erreurs de certification à réessayer" tone="rose" onClick={() => updateFilter('failed')} testId="fne-contract-kpi-failed" />
        <FneStickerWidget />
      </div>

      {/* Filters bar */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-3">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher (facture, contrat, client…)"
                className="pl-9 h-10 bg-white border-slate-200"
                data-testid="fne-contract-search"
              />
            </div>
            <div className="flex flex-wrap items-center border-b border-slate-100 lg:border-0">
              <TabPill active={filter === 'all'} label="Toutes" onClick={() => updateFilter('all')} count={c.total} testId="fne-contract-tab-all" />
              <TabPill active={filter === 'ready'} label="Prêtes" onClick={() => updateFilter('ready')} count={c.ready} testId="fne-contract-tab-ready" />
              <TabPill active={filter === 'not_certified'} label="Non certifiées" onClick={() => updateFilter('not_certified')} count={c.not_certified} testId="fne-contract-tab-not-certified" />
              <TabPill active={filter === 'certified'} label="Certifiées" onClick={() => updateFilter('certified')} count={c.certified} testId="fne-contract-tab-certified" />
              <TabPill active={filter === 'failed'} label="Échecs" onClick={() => updateFilter('failed')} count={c.failed} testId="fne-contract-tab-failed" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">Aucune facture contrat pour ce filtre.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/60 border-b border-slate-200 hover:bg-slate-50/60">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 pl-4">Facture / Contrat</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Période</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Client</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Montant TTC</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Statut facture</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">État FNE</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right pr-4">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => {
                    const cert = item.fne?.status;
                    const isCertified = ['certified', 'refunded_partial', 'refunded_total', 'refunded'].includes(cert);
                    return (
                      <TableRow key={item.id} className="border-b border-slate-100 hover:bg-emerald-50/20 transition-colors" data-testid={`fne-contract-row-${item.id}`}>
                        <TableCell className="py-3 align-top pl-4">
                          <Link to={`/admin/contracts/${item.contractId}`} className="font-mono text-sm font-medium text-blue-700 hover:underline">
                            {item.invoiceNumber || '—'}
                          </Link>
                          <div className="text-[11px] text-slate-400 font-mono">{item.contractNumber}</div>
                        </TableCell>
                        <TableCell className="py-3 align-top">
                          {fmtPeriod(item.billingPeriod) ? (
                            <div className="inline-flex items-center gap-1.5 text-sm text-slate-700" data-testid={`fne-contract-period-${item.id}`}>
                              <CalendarRange className="w-3.5 h-3.5 text-slate-400" />
                              <span className="font-medium">{fmtPeriod(item.billingPeriod)}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                          {item.dueDate && (
                            <div className="text-[11px] text-slate-400 mt-0.5">Échéance : {fmtShortDate(item.dueDate)}</div>
                          )}
                        </TableCell>
                        <TableCell className="py-3 align-top">
                          <div className="text-sm font-semibold text-slate-800">{item.client?.companyName || '—'}</div>
                          {item.client?.hasNcc ? (
                            <div className="text-[11px] text-emerald-700 mt-0.5">
                              <CheckCircle2 className="w-3 h-3 inline-block mr-0.5" />NCC OK <span className="font-mono text-slate-500">{item.client.ncc}</span>
                            </div>
                          ) : (
                            <div className="text-[11px] text-amber-700 mt-0.5 inline-flex items-center gap-0.5">
                              <AlertTriangle className="w-3 h-3" />NCC manquant → B2C
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-3 align-top text-sm font-medium text-slate-800">{fmtMoney(item.amount)}</TableCell>
                        <TableCell className="py-3 align-top">
                          <Badge variant="outline" className="text-xs">{INVOICE_STATUS_LABEL[item.invoiceStatus] || item.invoiceStatus}</Badge>
                        </TableCell>
                        <TableCell className="py-3 align-top">
                          <FneBadge status={cert} />
                          {item.fne?.reference && (
                            <div className="text-[10px] font-mono text-slate-500 mt-1 truncate max-w-[160px]" title={item.fne.reference}>{item.fne.reference}</div>
                          )}
                          {item.fne?.errorMessage && (
                            <div className="text-[10px] text-rose-600 mt-1 truncate max-w-[180px]" title={item.fne.errorMessage}>{item.fne.errorMessage}</div>
                          )}
                          {!isCertified && !item.certifiable && cert !== 'failed' && cert !== 'fne_error' && (
                            <div className="text-[10px] text-slate-500 mt-1" title={item.exclusionDetail || ''}>
                              {item.excluded
                                ? (item.exclusionLabel || 'Hors périmètre FNE')
                                : item.invoiceStatus === 'draft'
                                  ? 'Valider la facture d\'abord'
                                  : 'Pré-flight bloqué'}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-3 pr-4 align-top text-right">
                          {isCertified ? (
                            <div className="flex items-center justify-end gap-2">
                              {item.fne?.token && (
                                <a href={item.fne.token} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-emerald-700 underline hover:text-emerald-800" data-testid={`fne-contract-verify-${item.id}`}>
                                  Vérifier <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                              <a href={`${API}/fne/pdf/contract/${item.id}`} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-blue-700 underline hover:text-blue-900" data-testid={`fne-contract-pdf-${item.id}`}>
                                <FileText className="h-3 w-3" /> PDF
                              </a>
                              {(cert === 'certified' || cert === 'refunded_partial') && (
                                <Button size="sm" variant="outline" onClick={() => setRefundTarget(item)}
                                  className="h-8 px-3 text-xs border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`fne-contract-refund-${item.id}`} title="Émettre un avoir FNE">
                                  <FileMinus2 className="h-3.5 w-3.5 mr-1" /> Avoir
                                </Button>
                              )}
                            </div>
                          ) : item.certifiable || cert === 'failed' || cert === 'fne_error' ? (
                            <Button size="sm" onClick={() => setCertifyTarget(item)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 text-xs" data-testid={`fne-contract-certify-${item.id}`}>
                              <Shield className="h-3.5 w-3.5 mr-1" />
                              {cert === 'failed' || cert === 'fne_error' ? 'Réessayer' : 'Certifier'}
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" disabled
                              className="h-8 px-3 text-xs text-slate-400 border-slate-200 cursor-not-allowed" data-testid={`fne-contract-disabled-${item.id}`}
                              title={item.exclusionDetail || 'Validez/envoyez la facture pour la certifier'}>
                              <Shield className="h-3.5 w-3.5 mr-1" /> Indisponible
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {!loading && (data.total || 0) > PAGE_SIZE && (
          <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50/40 text-xs text-slate-600">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="h-8 w-8 p-0" data-testid="fne-contract-page-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span>Page {data.page || page} / {data.totalPages || 1}</span>
            <Button variant="ghost" size="sm" disabled={page >= (data.totalPages || 1)} onClick={() => setPage((p) => Math.min(data.totalPages || 1, p + 1))} className="h-8 w-8 p-0" data-testid="fne-contract-page-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </Card>

      {/* Certify modal */}
      {certifyTarget && (
        <FneCertifyModal
          open={!!certifyTarget}
          onClose={() => setCertifyTarget(null)}
          onSuccess={() => { setCertifyTarget(null); load(); }}
          targetType="contract"
          targetId={certifyTarget.id}
          targetLabel={certifyTarget.invoiceNumber}
        />
      )}

      {/* Refund modal */}
      {refundTarget && (
        <FneRefundModal
          open={!!refundTarget}
          onClose={() => setRefundTarget(null)}
          onSuccess={() => { setRefundTarget(null); load(); }}
          targetType="contract"
          targetId={refundTarget.id}
          targetLabel={refundTarget.invoiceNumber}
        />
      )}
    </div>
  );
}
